// src/services/isetSyncService.js
import { pb } from "./pocketbase";

const QUEUE_KEY = "iset_sync_queue";
const WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-iset-sync";
let isProcessing = false;
let syncInterval = null;

const getOperatorName = () => {
  try {
    return pb.authStore.model?.name || pb.authStore.model?.username || "Sistema";
  } catch (e) {
    return "Sistema";
  }
};

const getQueue = () => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const data = localStorage.getItem(QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error("Erro ao ler fila ISET do localStorage:", e);
    return [];
  }
};

const saveQueue = (queue) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error("Erro ao salvar fila ISET no localStorage:", e);
  }
};

export const isetSyncService = {
  // Enfileira um novo evento para sincronização
  enqueueEvent(event, orderNumber, itemId, additionalData = {}) {
    const cleanOrder = String(orderNumber).trim().replace(/[^0-9].*$/, "");
    if (!cleanOrder) {
      console.log(`[ISET Sync] Ignorando evento ${event} para pedido inválido/não numérico: ${orderNumber}`);
      return;
    }

    const queue = getQueue();

    // Evita duplicatas na fila pendente (mesmo pedido e mesmo evento)
    const exists = queue.some(
      (item) => item.orderNumber === cleanOrder && item.event === event && item.status === "pending"
    );

    if (exists) {
      console.log(`[ISET Sync] Evento ${event} para o pedido #${cleanOrder} já está na fila.`);
      return;
    }

    const newEvent = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      event,
      orderNumber: cleanOrder,
      itemId,
      timestamp: new Date().toISOString(),
      userName: getOperatorName(),
      status: "pending",
      attempts: 0,
      additionalData,
    };

    queue.push(newEvent);
    saveQueue(queue);
    console.log(`[ISET Sync] Evento enfileirado com sucesso:`, newEvent);

    // Tenta processar imediatamente
    this.processQueue();
  },

  // Processa a fila enviando as requisições para o webhook
  async processQueue() {
    if (isProcessing) return;
    if (typeof window === "undefined" || !navigator.onLine) {
      console.log("[ISET Sync] Dispositivo offline ou SSR. Aguardando conexão para enviar.");
      return;
    }

    let queue = getQueue();
    let queueChanged = false;

    // Sanitiza e limpa itens com erros persistentes na fila antes de enviar
    queue = queue.filter((item) => {
      const cleanNum = String(item.orderNumber).trim().replace(/[^0-9].*$/, "");
      if (!cleanNum) {
        queueChanged = true;
        console.log(`[ISET Sync] Removendo item ${item.id} da fila por não possuir número de pedido válido.`);
        return false;
      }
      if (item.orderNumber !== cleanNum) {
        item.orderNumber = cleanNum;
        queueChanged = true;
      }
      if (item.attempts >= 5) {
        queueChanged = true;
        console.warn(`[ISET Sync] Descartando item ${item.id} do pedido #${item.orderNumber} após ${item.attempts} tentativas malsucedidas.`);
        return false;
      }
      return true;
    });

    if (queueChanged) {
      saveQueue(queue);
    }

    const pendingItems = queue.filter((item) => item.status === "pending");
    if (pendingItems.length === 0) return;

    isProcessing = true;
    console.log(`[ISET Sync] Iniciando processamento de ${pendingItems.length} itens pendentes...`);

    // Atualiza status na fila local para "sending" para evitar concorrência/duplo envio
    pendingItems.forEach((item) => {
      item.status = "sending";
    });
    saveQueue(queue);

    // Processa os itens sequencialmente para manter consistência
    for (const item of pendingItems) {
      try {
        console.log(`[ISET Sync] Enviando evento "${item.event}" para o pedido #${item.orderNumber}...`);
        
        const response = await fetch(WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            event: item.event,
            orderNumber: item.orderNumber,
            itemId: item.itemId,
            timestamp: item.timestamp,
            userName: item.userName || "Sistema",
            ...(item.additionalData || {})
          }),
        });

        if (response.ok) {
          console.log(`[ISET Sync] Evento "${item.event}" para o pedido #${item.orderNumber} enviado com sucesso!`);
          // Remove o item da fila
          const currentQueue = getQueue();
          const updatedQueue = currentQueue.filter((q) => q.id !== item.id);
          saveQueue(updatedQueue);
        } else {
          throw new Error(`Resposta do servidor: ${response.status} ${response.statusText}`);
        }
      } catch (err) {
        console.error(`[ISET Sync] Falha ao enviar evento para o pedido #${item.orderNumber}:`, err);
        
        // Retorna o status para pendente e incrementa as tentativas
        const currentQueue = getQueue();
        const queueItem = currentQueue.find((q) => q.id === item.id);
        if (queueItem) {
          queueItem.status = "pending";
          queueItem.attempts += 1;
        }
        saveQueue(currentQueue);
      }
    }

    isProcessing = false;
  },

  // Inicializa o background worker
  startBackgroundSync() {
    if (typeof window === "undefined") return;

    if (syncInterval) {
      clearInterval(syncInterval);
    }

    console.log("[ISET Sync] Background Worker inicializado.");
    
    // Processa a fila no início
    this.processQueue();

    // Adiciona listener para quando voltar online
    window.addEventListener("online", () => {
      console.log("[ISET Sync] Conexão restabelecida. Processando fila...");
      this.processQueue();
    });

    // Roda periodicamente a cada 30 segundos
    syncInterval = setInterval(() => {
      this.processQueue();
    }, 30000);
  },
};
